import React, { useEffect, useReducer } from 'react';
import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { loadGoogleFont } from '../../../../utils/googleFonts';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Header from '../Redesign/Common/Header';
import MenuCard from '../Redesign/Common/MenuCard';
import { useParams } from 'react-router-dom';
import { useQuery as useRestQuery } from 'react-query';
import { getMenuQuery } from '../Menu/queries';
import { initialStateKiosk, reducer } from '../Redesign/Common/State';
import { calculateTotalPrice } from '../../../../utils/priceCalculator';
import Categories from '../Redesign/Common/Categories';
import KioskCart from './Common/KioskCart';
import InactivityModal from '../../../../components/Global/InactivityModal/index';
import { useInactivityModal } from '../../../../hooks/useInactivityModal';
import StartOrderCoverScreen from '../../../../components/Global/Location/Common/components/StartOrderCoverScreen';
import { useStartOrderCoverScreen } from '../../../../hooks/useStartOrderCoverScreen';
import { useOverscrollBackgroundColor } from '../../../../hooks/useOverscrollBackgroundColor';

const useStyles = makeStyles((theme: Theme) => ({
  layout: {
    backgroundColor: theme.palette.background.default,
    height: '100vh',
    overflow: 'auto',
  },
  pageContainer: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
  },
  menuSection: {
    flex: 1,
    paddingRight: theme.spacing(3),
  },
  menuGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, 1fr)',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  categoryHeader: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    margin: theme.spacing(2, 0),
    color: theme.palette.text.primary,
  },
  cartContainer: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    boxShadow: theme.shadows[1],
    position: 'sticky',
    top: theme.spacing(2),
    height: 'fit-content',
  },
  categoriesBar: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 0),
    textAlign: 'center',
  },
}));

interface UrlParams {
  organisation_id: string;
  location_id: string;
}

interface MenuData {
  categories: Category[];
  location_info: any;
}

interface Category {
  id: string;
  name: string;
  category_type: 'DISH' | 'DRINK';
  dish_assignments: { dish: any }[];
  drink_assignments: { drink: any }[];
}

const INACTIVITY_TIMEOUT = 30000;

const Kiosk: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialStateKiosk);
  const { cart, orderDetails } = state;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { organisation_id, location_id } = useParams<UrlParams>();

  const { data: menuData } = useRestQuery<MenuData>({
    queryKey: 'menu-data',
    queryFn: () => getMenuQuery(organisation_id, location_id),
    onSuccess: (data) => {
      if (data?.location_info?.google_font_family) {
        loadGoogleFont(data.location_info.google_font_family, ['400', '500', '600', '700']);
      }
    },
  });

  useEffect(() => {
    let fontStyleTag: HTMLStyleElement | null = null;
    if (menuData?.location_info?.google_font_family) {
      fontStyleTag = document.createElement('style');
      fontStyleTag.setAttribute('data-google-font-family', 'true');
      fontStyleTag.innerHTML = `body, body * { font-family: '${menuData.location_info.google_font_family}', sans-serif !important; }`;
      document.head.appendChild(fontStyleTag);
    }
    return () => {
      if (fontStyleTag) document.head.removeChild(fontStyleTag);
    };
  }, [menuData?.location_info?.google_font_family]);

  const color = menuData?.location_info?.ordering_system_primary_color || theme.palette.background.default;
  useOverscrollBackgroundColor(color);

  const [selectedCategory, setSelectedCategory] = React.useState<string>('');
  const [shouldShowStartOrderScreen, setShouldShowStartOrderScreen] = React.useState(false);

  const sortCategories = (categories: Category[]) => {
    return categories.sort((a, b) => {
      if (a.category_type === 'DISH' && b.category_type === 'DRINK') return -1;
      if (a.category_type === 'DRINK' && b.category_type === 'DISH') return 1;
      //@ts-ignore
      return a.order_index - b.order_index;
    });
  };

  useEffect(() => {
    if (menuData?.categories?.[0]?.id) {
      const sortedCategories = sortCategories([...menuData.categories]);
      setSelectedCategory(sortedCategories[0].id);
    }
  }, [menuData]);

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };
  const filteredMenuItems = React.useMemo(() => {
    const selectedCategoryData = menuData?.categories.find((category) => category.id === selectedCategory);

    if (!selectedCategoryData) return [];

    let items =
      selectedCategoryData.category_type === 'DISH'
        ? selectedCategoryData.dish_assignments.map((item) => item.dish)
        : selectedCategoryData.drink_assignments.map((item) => item.drink);

    return items.sort((a, b) => a.order_index - b.order_index);
  }, [menuData, selectedCategory]);
  //@ts-ignore
  const totalPrice = calculateTotalPrice(cart, menuData);

  const { inactivityOpen, inactivityModalKey, handleContinue, handleExit, handleModalClose } = useInactivityModal({
    isCartNotEmpty: Object.keys(cart).length > 0,
    onExit: () => window.location.reload(),
    inactivityTimeout: INACTIVITY_TIMEOUT,
  });

  const { isOpen: showStartScreen, close: handleStartOrder } = useStartOrderCoverScreen(cart, shouldShowStartOrderScreen);

  return (
    <>
      {menuData?.location_info && showStartScreen && <StartOrderCoverScreen onStart={handleStartOrder} locationInfo={menuData.location_info} location_id={location_id} />}
      {!showStartScreen && menuData?.location_info && (
        <div className={classes.layout}>
          <Header locationInfo={menuData.location_info} location_id={location_id} dispatch={dispatch} state={state} hideOpeningTimesAndContact />
          {/* Categories Bar */}
          <Box className={classes.categoriesBar}>
            {menuData?.categories && (
              <Categories categories={sortCategories([...menuData.categories])} selectedCategory={selectedCategory} onCategorySelect={handleCategorySelect} menuData={menuData} />
            )}
          </Box>
          {/* Main Content */}
          <Container maxWidth={false} className={classes.pageContainer}>
            <Grid container spacing={4}>
              {/* Menu Section */}
              <Grid item xs={12} md={8} className={classes.menuSection}>
                {selectedCategory && (
                  <Typography
                    className={classes.categoryHeader}
                    style={{
                      fontFamily: menuData?.location_info?.google_font_family ? `'${menuData.location_info.google_font_family}', sans-serif` : 'inherit',
                      fontWeight: 'bold',
                    }}>
                    {menuData?.categories.find((category) => category.id === selectedCategory)?.name}
                  </Typography>
                )}
                <div className={classes.menuGrid}>
                  {filteredMenuItems.map((item) => (
                    <MenuCard
                      key={item.id}
                      primary={menuData?.location_info?.ordering_system_primary_color}
                      secondary={menuData?.location_info?.ordering_system_secondary_color}
                      item={{
                        ...item,
                        imageUrl: item.image_added ? `${process.env.REACT_APP_AWS_IMAGES_URL}/${item.id}.jpeg` : ``,
                      }}
                      cart={cart}
                      dispatch={dispatch}
                    />
                  ))}
                </div>
              </Grid>
              {/* Cart Section */}
              <Grid item xs={12} md={4}>
                <div className={classes.cartContainer}>
                  <KioskCart
                    cart={cart}
                    dispatch={dispatch}
                    menuData={menuData}
                    totalPrice={totalPrice}
                    //@ts-ignore
                    orderDetails={orderDetails}
                    location_id={location_id}
                    organisation_id={organisation_id}
                    onOrderCompleted={() => setShouldShowStartOrderScreen(true)}
                  />
                </div>
              </Grid>
            </Grid>
          </Container>
        </div>
      )}
      <InactivityModal
        key={inactivityModalKey}
        open={inactivityOpen}
        onContinue={handleContinue}
        onExit={handleExit}
        onClose={handleModalClose}
        primaryColor={menuData?.location_info?.ordering_system_primary_color}
        secondaryColor={menuData?.location_info?.ordering_system_secondary_color}
        disableScrollLock
      />
      {/*{isPaymentProcessing && <LoadingOverlay message="Processing your payment..." />}*/}
    </>
  );
};

export default Kiosk;
